/**
 * Consulta de garantía por chasis o cédula.
 *
 * Una cédula puede tener varias motos: se listan para elegir. La ficha
 * muestra vigencia, la alerta de omitidos pendientes de decisión (gerente:
 * mantener vigente / marcar perdida, con motivo a la bitácora) y la línea de
 * tiempo de mantenimientos derivada del plan copiado al vender.
 */
import React, { useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import SearchIcon from "@mui/icons-material/Search";
import BuildOutlinedIcon from "@mui/icons-material/BuildOutlined";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import PostAddOutlinedIcon from "@mui/icons-material/PostAddOutlined";
import VerifiedOutlinedIcon from "@mui/icons-material/VerifiedOutlined";
import { FP, StatusPill, EstadoVacio, ConfirmarDialog, fpInputSx } from "components/financepro";
import { useApi } from "lib/api";
import { useLogoEmpresa } from "lib/multiempresa";
import {
  resumen,
  serviciosExtra,
  fmtFecha,
  ESTADOS_SERVICIO,
  ESTADOS_GARANTIA,
  coberturaPiezas,
} from "./calendario";
import {
  btnSolid,
  btnOutline,
  sectionBox,
  etiquetaSeccion,
  fmtPrecio,
  fmtMonto,
  CONDICION,
} from "./comunes";
import RegistrarServicio from "./registrarservicio";
import { abrirCertificado } from "./certificado";

function Metrica({ label, valor }) {
  return (
    <Box sx={{ bgcolor: FP.color.surface, borderRadius: 1.8, px: 1.8, py: 1.4, height: "100%" }}>
      <Typography sx={{ ...etiquetaSeccion, fontSize: "0.64rem", mb: 0.4 }}>{label}</Typography>
      <Typography sx={{ fontWeight: 800, fontSize: "1.15rem", color: FP.color.textPrimary }}>
        {valor}
      </Typography>
    </Box>
  );
}

function FilaServicio({ s, ultimo }) {
  const est = ESTADOS_SERVICIO[s.estado];
  let detalle = `Ventana ${fmtFecha(s.desde)} – ${fmtFecha(s.hasta)}`;
  if (s.servicio) {
    const km = s.servicio.km != null ? ` · ${fmtMonto(s.servicio.km)} km` : "";
    detalle = `Hecho ${fmtFecha(s.servicio.fecha)}${km} · ${s.servicio.tecnico} · ${
      s.servicio.sucursal
    }`;
  }
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "28px 1fr auto", sm: "28px 1fr 110px 120px" },
        gap: 1.5,
        alignItems: "center",
        py: 1.3,
        borderBottom: ultimo ? "none" : `1px solid ${FP.color.border}`,
      }}
    >
      <Typography sx={{ color: FP.color.textMuted, fontWeight: 700 }}>{s.numero}</Typography>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontWeight: 700, fontSize: "0.9rem", color: FP.color.textPrimary }}>
          {s.nombre} · {fmtPrecio(s.precio)}
        </Typography>
        <Typography sx={{ fontSize: "0.78rem", color: FP.color.textSecondary }}>
          {detalle}
        </Typography>
        {s.decision ? (
          <Typography
            sx={{ fontSize: "0.78rem", color: FP.color.textSecondary, fontStyle: "italic" }}
          >
            Gerente: {s.decision.tipo === "perdida" ? "marcó perdida" : "mantuvo vigente"} el{" "}
            {fmtFecha(s.decision.fecha)} — {s.decision.motivo}
          </Typography>
        ) : null}
      </Box>
      <Typography
        sx={{
          fontSize: "0.85rem",
          color: FP.color.textSecondary,
          display: { xs: "none", sm: "block" },
        }}
      >
        {fmtFecha(s.fecha)}
      </Typography>
      <Box sx={{ textAlign: "right" }}>
        <StatusPill label={est.label} variant={est.variant} />
      </Box>
    </Box>
  );
}

export default function ConsultaGarantia({ config, hoy, busquedaInicial, toast }) {
  const { post } = useApi();
  // Logo de configuración para el certificado ("" si el tenant no tiene).
  const logo = useLogoEmpresa(0);
  const permisos = config.permisos || {};
  const [texto, setTexto] = useState(busquedaInicial?.texto || "");
  const [datos, setDatos] = useState({ garantias: [], servicios: [] });
  const [buscado, setBuscado] = useState(false);
  const [cargando, setCargando] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [seleccionId, setSeleccionId] = useState(null);
  const [registrarAbierto, setRegistrarAbierto] = useState(false);
  const [decision, setDecision] = useState(null); // { tipo, numero }
  const { garantias, servicios } = datos;

  // `mantener`: id a dejar seleccionado tras recargar (registrar / decidir).
  const buscar = async (valor = texto, mantener = null) => {
    const q = String(valor || "").trim();
    if (q.replace(/[\s-]/g, "").length < 4) {
      toast.aviso("Escriba al menos 4 caracteres del chasis o la cédula");
      return;
    }
    setCargando(true);
    try {
      const { ok, data } = await post("/api/garantias_buscar", { texto: q });
      if (!ok) {
        toast.error(data?.Error || "No se pudo buscar la garantía");
        return;
      }
      const encontrados = data.garantias || [];
      setDatos({ garantias: encontrados, servicios: data.servicios || [] });
      setBuscado(true);
      if (mantener && encontrados.some((g) => g.id === mantener)) setSeleccionId(mantener);
      else setSeleccionId(encontrados.length === 1 ? encontrados[0].id : null);
    } finally {
      setCargando(false);
    }
  };

  // Llegada desde el reporte ("Ver"): busca por chasis y abre la ficha.
  useEffect(() => {
    if (busquedaInicial?.texto) {
      setTexto(busquedaInicial.texto);
      buscar(busquedaInicial.texto);
    }
  }, [busquedaInicial]); // eslint-disable-line react-hooks/exhaustive-deps

  const registrarServicio = async (servicio) => {
    setGuardando(true);
    try {
      const { ok, data } = await post("/api/garantias_servicio", {
        ...servicio,
        garantia: servicio.garantiaId,
      });
      if (!ok) {
        toast.error(data?.Error || "No se pudo registrar el servicio");
        return;
      }
      toast.exito("Servicio registrado");
      setRegistrarAbierto(false);
      await buscar(texto, servicio.garantiaId);
    } finally {
      setGuardando(false);
    }
  };

  const decidir = async (motivo) => {
    setGuardando(true);
    try {
      const { ok, data } = await post("/api/garantias_decidir", {
        garantia: seleccionId,
        numero: decision.numero,
        tipo: decision.tipo,
        motivo: motivo.trim(),
      });
      if (!ok) {
        toast.error(data?.Error || "No se pudo registrar la decisión");
        return;
      }
      toast.exito(
        decision.tipo === "perdida" ? "Garantía marcada como perdida" : "Garantía mantenida vigente"
      );
      setDecision(null);
      await buscar(texto, seleccionId);
    } finally {
      setGuardando(false);
    }
  };

  const garantia = garantias.find((g) => g.id === seleccionId) || null;
  const res = useMemo(
    () => (garantia ? resumen(garantia, servicios, hoy) : null),
    [garantia, servicios, hoy]
  );
  const extras = garantia ? serviciosExtra(garantia, servicios) : [];
  const activa = res && !["anulada", "cerrada"].includes(res.estado);
  const resultados = buscado ? garantias : null;

  return (
    <Stack spacing={2.5}>
      <Box sx={sectionBox}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25}>
          <TextField
            fullWidth
            label="Chasis o cédula"
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && buscar()}
            sx={fpInputSx}
          />
          <Button
            onClick={() => buscar()}
            disabled={cargando}
            startIcon={<SearchIcon />}
            sx={{ ...btnSolid(), px: 3, minWidth: 130 }}
          >
            Buscar
          </Button>
        </Stack>
        {resultados && resultados.length > 1 ? (
          <Stack direction="row" spacing={1} sx={{ mt: 2, flexWrap: "wrap", gap: 1 }}>
            {resultados.map((g) => (
              <Button
                key={g.id}
                onClick={() => setSeleccionId(g.id)}
                sx={g.id === seleccionId ? btnSolid() : btnOutline(FP.color.textSecondary)}
              >
                {g.marca} {g.modelo} · {g.chasis.slice(-6)}
              </Button>
            ))}
          </Stack>
        ) : null}
      </Box>

      {resultados && resultados.length === 0 ? (
        <Box sx={sectionBox}>
          <EstadoVacio
            titulo="Sin garantías"
            subtitulo="No hay garantías para ese chasis o cédula."
          />
        </Box>
      ) : null}

      {!resultados ? (
        <Box sx={sectionBox}>
          <EstadoVacio
            icono={VerifiedOutlinedIcon}
            titulo="Busque una moto"
            subtitulo="Escriba el chasis (o parte) o la cédula del cliente."
          />
        </Box>
      ) : null}

      {garantia && res ? (
        <Box sx={sectionBox}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{ fontWeight: 800, fontSize: "1.05rem", color: FP.color.textPrimary }}
              >
                {garantia.cliente} · {garantia.cedula}
              </Typography>
              <Typography sx={{ fontSize: "0.85rem", color: FP.color.textSecondary }}>
                {garantia.marca} {garantia.modelo} {garantia.color.toLowerCase()} ·{" "}
                {CONDICION[garantia.condicion].toLowerCase()} · {garantia.chasis}
                {garantia.noprest ? ` · préstamo ${garantia.noprest}` : " · contado"} · vendida{" "}
                {fmtFecha(garantia.fechaVenta)}
              </Typography>
              <Typography sx={{ fontSize: "0.78rem", color: FP.color.textMuted }}>
                Sucursal {garantia.sucursalNombre} · política {garantia.politica || "—"}
                {garantia.telefono ? ` · tel. ${garantia.telefono}` : ""}
              </Typography>
            </Box>
            <StatusPill
              label={ESTADOS_GARANTIA[res.estado].label}
              variant={ESTADOS_GARANTIA[res.estado].variant}
              sx={{ height: 26, fontSize: "0.74rem" }}
            />
          </Stack>

          <Grid container spacing={1.5} sx={{ mt: 1 }}>
            <Grid item xs={6} sm={3}>
              <Metrica label="Vence" valor={fmtFecha(res.fin)} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <Metrica
                label="Quedan"
                valor={res.estado === "vigente" ? `${res.diasRestantes} días` : "—"}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <Metrica
                label="Meses"
                valor={`${garantia.meses} (${CONDICION[garantia.condicion].toLowerCase()})`}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <Metrica label="Servicios" valor={`${res.hechos} de ${res.total}`} />
            </Grid>
          </Grid>

          {garantia.piezas && garantia.piezas.length ? (
            <Box sx={{ mt: 2 }}>
              <Typography sx={{ ...etiquetaSeccion, mb: 0.5 }}>
                Garantía limitada a piezas
              </Typography>
              <Stack direction="row" sx={{ flexWrap: "wrap", gap: 1 }}>
                {coberturaPiezas(garantia, hoy).map((x) => (
                  <Box
                    key={x.nombre}
                    sx={{
                      border: `1px solid ${FP.color.border}`,
                      borderRadius: 1.8,
                      px: 1.5,
                      py: 0.8,
                      opacity: x.vigente && activa ? 1 : 0.6,
                    }}
                  >
                    <Typography sx={{ fontWeight: 700, fontSize: "0.85rem" }}>
                      {x.nombre}
                    </Typography>
                    <Typography sx={{ fontSize: "0.75rem", color: FP.color.textSecondary }}>
                      {x.meses} meses · {x.vigente ? "hasta" : "venció el"} {fmtFecha(x.vence)}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Box>
          ) : null}

          {res.alertas.map((s) => (
            <Stack
              key={s.numero}
              direction={{ xs: "column", sm: "row" }}
              alignItems={{ xs: "flex-start", sm: "center" }}
              spacing={1.5}
              sx={{
                mt: 2,
                p: 1.5,
                borderRadius: 1.8,
                bgcolor: FP.color.warningSoft,
                border: "1px solid #FCD34D",
                color: "#92400E",
              }}
            >
              <WarningAmberRoundedIcon />
              <Typography sx={{ flex: 1, fontSize: "0.88rem", fontWeight: 600 }}>
                Omitió el {s.nombre.toLowerCase()} (la ventana cerró el {fmtFecha(s.hasta)}). Un
                gerente debe decidir.
              </Typography>
              {permisos.decidir ? (
                <>
                  <Button
                    size="small"
                    onClick={() => setDecision({ tipo: "vigente", numero: s.numero })}
                    sx={btnOutline("#92400E")}
                  >
                    Mantener vigente
                  </Button>
                  <Button
                    size="small"
                    onClick={() => setDecision({ tipo: "perdida", numero: s.numero })}
                    sx={btnSolid(FP.color.danger, "#BE123C")}
                  >
                    Marcar perdida
                  </Button>
                </>
              ) : null}
            </Stack>
          ))}

          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mt: 2.5, mb: 0.5 }}
          >
            <Typography sx={etiquetaSeccion}>Mantenimientos</Typography>
            {permisos.registrar && activa ? (
              <Button
                size="small"
                startIcon={<BuildOutlinedIcon />}
                onClick={() => setRegistrarAbierto(true)}
                sx={btnOutline()}
              >
                Registrar servicio
              </Button>
            ) : null}
          </Stack>
          {res.calendario.map((s, i) => (
            <FilaServicio
              key={s.numero}
              s={s}
              ultimo={i === res.calendario.length - 1 && !extras.length}
            />
          ))}
          {extras.map((x, i) => (
            <Box
              key={x.id}
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "28px 1fr auto", sm: "28px 1fr 110px 120px" },
                gap: 1.5,
                alignItems: "center",
                py: 1.3,
                borderBottom: i === extras.length - 1 ? "none" : `1px solid ${FP.color.border}`,
              }}
            >
              <Typography sx={{ color: FP.color.textMuted, fontWeight: 700 }}>+</Typography>
              <Box>
                <Typography sx={{ fontWeight: 700, fontSize: "0.9rem" }}>
                  Servicio extra · {fmtPrecio(x.costo)}
                </Typography>
                <Typography sx={{ fontSize: "0.78rem", color: FP.color.textSecondary }}>
                  {x.observaciones || "—"} · {x.tecnico}
                </Typography>
              </Box>
              <Typography
                sx={{
                  fontSize: "0.85rem",
                  color: FP.color.textSecondary,
                  display: { xs: "none", sm: "block" },
                }}
              >
                {fmtFecha(x.fecha)}
              </Typography>
              <Box sx={{ textAlign: "right" }}>
                <StatusPill label="Extra" variant="neutral" />
              </Box>
            </Box>
          ))}

          <Stack direction="row" spacing={1} sx={{ mt: 2.5, flexWrap: "wrap", gap: 1 }}>
            <Button
              size="small"
              startIcon={<PrintOutlinedIcon />}
              onClick={() =>
                abrirCertificado({
                  garantia,
                  politica: { cubre: garantia.cubre, noCubre: garantia.noCubre },
                  empresa: config.compania || "",
                  logo,
                  hoy,
                })
              }
              sx={btnOutline()}
            >
              Certificado
            </Button>
            <Button
              size="small"
              disabled
              startIcon={<PostAddOutlinedIcon />}
              sx={btnOutline(FP.color.textMuted)}
            >
              Nuevo reclamo (fase 2)
            </Button>
          </Stack>
        </Box>
      ) : null}

      <RegistrarServicio
        abierto={registrarAbierto}
        garantia={garantia}
        calendario={res?.calendario}
        hoy={hoy}
        onCerrar={() => setRegistrarAbierto(false)}
        guardando={guardando}
        onRegistrar={registrarServicio}
      />

      <ConfirmarDialog
        abierto={!!decision}
        titulo={
          decision?.tipo === "perdida" ? "Marcar garantía perdida" : "Mantener garantía vigente"
        }
        mensaje={
          decision?.tipo === "perdida"
            ? "La garantía deja de cubrir reclamos. Queda registrado quién lo decidió y por qué."
            : "La alerta de este servicio deja de salir en el reporte. Queda registrado quién lo decidió y por qué."
        }
        textoConfirmar={decision?.tipo === "perdida" ? "Marcar perdida" : "Mantener vigente"}
        destructivo={decision?.tipo === "perdida"}
        razonObligatoria
        etiquetaRazon="Motivo (obligatorio)"
        cargando={guardando}
        onCerrar={() => setDecision(null)}
        onConfirmar={decidir}
      />
    </Stack>
  );
}
